import {
  BannerColorLabels,
  BannerLayoutPlacements,
  ConfigKeys,
  ConfigValues,
  Countries,
  defaultConfig,
  InputRegexPatterns,
  LogoTypeLabels,
  MessagingAttributes,
  PayPalHostedExperiences,
  productPriceMap,
  SelectionGroupOptions,
  StyleCustomizations,
  TextColorLabels,
  TextSizeLabels,
  UpstreamMessagingPlacementMap
} from '@MerchantConfigurator/constants';
import { filterArrayByMatchingValue } from '@MerchantConfigurator/utils/filterUtils';
import { validateInputParameter } from '@MerchantConfigurator/utils/validations';

const filterConfigByPlacements = ({ config, placements }) => {
  return Object.fromEntries(Object.entries(config).filter(([key]) => placements.indexOf(key) > -1));
};

const identifyAndReplaceUnsupportedConfig = ({
  placementConfigKeys,
  santizedMerchantConfig,
  placement,
  placementConfigIndex,
  placementsWithLegacyConfig
}) =>
  placementConfigKeys.reduce((updatedConfig, configKey) => {
    const configKeyString = configKey.toString();
    const merchantPlacementConfig =
      placementConfigIndex >= 0
        ? santizedMerchantConfig[placement][placementConfigIndex][configKeyString]
        : santizedMerchantConfig[placement][configKeyString];
    const defaultPlacementConfig = defaultConfig[placement][configKeyString];
    let updatedConfigValue = merchantPlacementConfig;

    /*
    Check if the config key passed has a dropdown option.
    if yes, the config key values are validated below.
    */
    if (SelectionGroupOptions[configKeyString]) {
      const configValueSupported = SelectionGroupOptions[configKeyString].find(
        (supportedConfig) => merchantPlacementConfig === supportedConfig.value
      );
      if (!configValueSupported) {
        updatedConfigValue = defaultPlacementConfig;
        placementsWithLegacyConfig.add(placement);
      }
    } else {
      /* 
    The config key passed has NO dropdown option. 
    Those config key, values are validated below. 
    */
      if (
        configKeyString !== ConfigKeys.status &&
        configKeyString !== ConfigKeys.message_reference &&
        merchantPlacementConfig !== defaultPlacementConfig
      ) {
        updatedConfigValue = defaultPlacementConfig;
        placementsWithLegacyConfig.add(placement);
      } else {
        const typeOfConfigValueisArray = Array.isArray(ConfigValues[configKeyString]);
        if (typeOfConfigValueisArray) {
          const sanitizedInput =
            santizedMerchantConfig[placement][placementConfigIndex][configKeyString];

          updatedConfigValue = ConfigValues[configKeyString].includes(sanitizedInput)
            ? santizedMerchantConfig[placement][placementConfigIndex][configKeyString]
            : null;
        } else {
          updatedConfigValue = ConfigValues.status[merchantPlacementConfig]
            ? merchantPlacementConfig
            : defaultPlacementConfig;
        }
      }
    }

    updatedConfig[configKeyString] = updatedConfigValue;
    return updatedConfig;
  }, {});

export const replaceLegacyConfig = ({ santizedMerchantConfig = {} }) => {
  let updatedConfig = {};
  const placementsWithLegacyConfig = new Set();
  const placements = Object.keys(santizedMerchantConfig);

  if (placements.length) {
    updatedConfig = placements.reduce((result, placement) => {
      //For placements that have more than one configuration. Example: custom_placement
      if (Array.isArray(santizedMerchantConfig[placement])) {
        const updatedPlacement = santizedMerchantConfig[placement].map(
          (config, placementConfigIndex) => {
            let unsupportedValue = false;
            const placementConfigKeys = Object.keys(config);

            const newConfig = identifyAndReplaceUnsupportedConfig({
              placementConfigKeys,
              santizedMerchantConfig,
              placement,
              placementConfigIndex,
              placementsWithLegacyConfig
            });

            //Below code is to remove the unsupported config for the respective placement
            Object.keys(newConfig).forEach((value) => {
              if (!newConfig[value]) unsupportedValue = true;
            });

            return unsupportedValue
              ? null
              : {
                  ...config,
                  ...newConfig
                };
          }
        );

        const validConfigs = filterArrayByMatchingValue({
          array: updatedPlacement,
          value: null
        });

        result[placement] = validConfigs;
      } else {
        const placementConfigKeys = Object.keys(santizedMerchantConfig[placement]);

        const newConfig = identifyAndReplaceUnsupportedConfig({
          placementConfigKeys,
          santizedMerchantConfig,
          placement,
          placementsWithLegacyConfig
        });

        result[placement] = {
          ...defaultConfig[placement],
          ...santizedMerchantConfig[placement],
          ...newConfig
        };
      }

      Object.keys(result[placement]).forEach(function (key) {
        if (typeof result[placement][key] === 'undefined') {
          delete result[placement][key];
        }
      });

      return result;
    }, {});
  }

  return {
    containsLegacyConfig: placementsWithLegacyConfig.size > 0,
    placementsWithLegacyConfig: Array.from(placementsWithLegacyConfig),
    updatedConfig
  };
};

const isPayLaterCountry = (country) => !!Countries[country];

const generateHeaderScript = ({ bnCode, clientId, country, currency }) => {
  if (!validateInputParameter(bnCode, InputRegexPatterns.bnCode)) {
    return '';
  }

  const headerScript =
    country === Countries.US || !isPayLaterCountry(country)
      ? `<script
src="https://www.paypal.com/sdk/js?client-id=${clientId}&components=messages"
data-namespace="PayPalSDK"
data-partner-attribution-id="${bnCode}">
</script>`
      : `<script
src="https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency}&components=messages"
data-namespace="PayPalSDK"
data-partner-attribution-id="${bnCode}">
</script>`;

  return headerScript;
};

const generateMessageStylesProperties = (messageConfig) => {
  return Object.entries(messageConfig)
    .filter(
      ([key]) =>
        key !== MessagingAttributes.status &&
        key !== MessagingAttributes.placement &&
        // We do not want to inject attributes that are undefined, null, or empty string
        !!messageConfig[key]
    )
    .reduce(
      (accumulator, [key, val]) => `${accumulator}
    data-pp-style-${key.replace(/\./g, '-')}="${val}"`,
      ''
    );
};

const generateMessagingCodeSnippet = ({ messageConfig, offerType, productPrice }) => {
  if (messageConfig?.status === ConfigValues.status.disabled) {
    return `<div></div>`;
  }

  const offerTypeGeneric = offerType === 'PAY_LATER_GENERIC';
  const placement = UpstreamMessagingPlacementMap[messageConfig.placement];

  return `<div
    data-pp-message${generateMessageStylesProperties(messageConfig)}
    ${!offerTypeGeneric && `data-pp-amount=${productPrice || 'ENTER_VALUE_HERE'}`}
    ${
      offerType
        ? !offerTypeGeneric && `data-pp-offer=${offerType}`
        : `data-pp-placement=${placement}`
    }>
</div>`;
};

const getOptions = ({ attribute, content, countryCode = Countries.US }) => {
  return SelectionGroupOptions[attribute]
    .filter((option) => {
      return !(
        attribute === MessagingAttributes.color &&
        countryCode !== Countries.US &&
        option.value === ConfigValues.color.whiteNoBorder
      );
    })
    .map((option) => {
      return {
        label: content[option.label],
        value: option.value
      };
    });
};

const getPriceOfferTypesList = ({ country = 'US' }) => {
  return Object.entries(productPriceMap[country]);
};

const convertMessagingConfigToQueryParams = ({ messagingConfig }) => {
  const queryParams = {};

  Object.keys(messagingConfig).forEach((placement) => {
    if (Array.isArray(messagingConfig[placement]) && messagingConfig[placement].length > 0) {
      messagingConfig[placement].forEach((value) => {
        const placementName = value.message_reference;
        queryParams[`${placementName}_enabled`] = value.status === ConfigValues.status.enabled;
      });
    } else {
      queryParams[`${placement}_enabled`] =
        messagingConfig[placement].status === ConfigValues.status.enabled;

      if (BannerLayoutPlacements.has(placement)) {
        queryParams[`${placement}_banner_color`] =
          BannerColorLabels[messagingConfig[placement].color];
        queryParams[`${placement}_banner_size`] = messagingConfig[placement].ratio;
      } else {
        queryParams[`${placement}_logo_position`] = messagingConfig[placement]['logo-position'];
        queryParams[`${placement}_logo_type`] =
          LogoTypeLabels[messagingConfig[placement]['logo-type']];
        queryParams[`${placement}_text_color`] =
          TextColorLabels[messagingConfig[placement]['text-color']];
        queryParams[`${placement}_text_size`] =
          TextSizeLabels[messagingConfig[placement]['text-size']];
      }
    }
  });

  return queryParams;
};

const getCustomizationQueryParams = ({ styleOverrides }) => {
  const queryParams = {};

  const customizedStyles = new Set(Object.keys(styleOverrides));

  Object.keys(StyleCustomizations).forEach((customizableElement) => {
    queryParams[StyleCustomizations[customizableElement]] =
      customizedStyles.has(customizableElement);
  });

  return queryParams;
};

const getEligibleCountries = () => {
  return Object.values(Countries);
};

const getInitialCounters = ({ placements }) => {
  const counters = {
    desktop_preview_clicked: 0,
    message_preview_clicked: 0,
    mobile_preview_clicked: 0,
    preview_arrow_left_clicked: 0,
    preview_arrow_right_clicked: 0,
    preview_color_clicked: 0,
    reset_customizations_clicked: 0,
    total_configs_updated: 0
  };

  placements.forEach((placement, index) => {
    counters[`${placement}_checkbox_clicked`] = 0;
    counters[`${placement}_configs_updated`] = 0;
    counters[`${placement}_viewed`] = index === 0 ? 1 : 0;
  });

  return counters;
};

export const shouldShowPartnerExperience = ({ partnerName }) =>
  !PayPalHostedExperiences.includes(partnerName);

export const getDefaultConfigs = ({ placements = [], showPartnerExperience = true }) => {
  const defaultConfigsForSupportedPlacements = filterConfigByPlacements({
    config: defaultConfig,
    placements
  });

  //To set the status enable for PayPal hosted experience. Example: DevDocs
  if (!showPartnerExperience) {
    for (const placement of placements) {
      const placementConfig = defaultConfigsForSupportedPlacements[placement] || {};
      placementConfig.status = ConfigValues.status.enabled;
    }
  }
  return defaultConfigsForSupportedPlacements;
};

export {
  convertMessagingConfigToQueryParams,
  filterConfigByPlacements,
  generateHeaderScript,
  generateMessagingCodeSnippet,
  getCustomizationQueryParams,
  getEligibleCountries,
  getInitialCounters,
  getOptions,
  getPriceOfferTypesList
};
